/* eslint-disable react/jsx-props-no-spreading */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react';
import { Controller, useForm, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ScrollSmoother } from 'gsap/ScrollSmoother';

import { postContact, putContactOrigin } from '@/services/contact/contactService';
import Button from '@/components/atoms/Button/Button';
import ToggleField from '@/components/molecules/ToggleField/ToggleField';
import MixedTitle from '@/components/molecules/MixedTitle/MixedTitle';
import OptionButton from '@/components/atoms/OptionButton/OptionButton';
import ThankYouMessage from '@/components/organisms/ThankYouMessage/ThankYouMessage';
import Picto from '@/components/atoms/Picto/Picto';
import { ColorThemeEnum } from '@/enum/ColorThemeEnum';
import { Option } from '@/types/Option';
import { ContactType } from '@/views/Contact/ContactType';
import TypewriterText from '@/components/atoms/TypewriterText/TypewriterText';
import cn from '@/utils/cn';
import useResponsiveQueries from '@/hooks/utils/useResponsiveQueries';
import styles from './ContactInteractiveForm.module.css';

const contactSchema = z.object({
  firstname: z.string().min(1, { message: 'Le prénom est requis' }),
  lastname: z.string().optional(),
  job: z.string().optional(),
  company: z.string().optional(),
  email: z.email({ message: 'L\'adresse email est invalide' }).min(1, { message: 'L\'adresse email est requise' }),
  phone: z.string().optional(),
  expertise: z.string().min(1, { message: 'Le choix d\'une option est requise' }),
  type: z.string().optional(),
  budget: z.string().optional(),
  comment: z.string().optional(),
  isUrgent: z.boolean().optional(),
  marketingConsent: z.boolean().refine((val) => val === true, { message: 'Le consentement est requis' }),
  files: z.array(z.instanceof(File)).optional(),
});

type FormData = z.infer<typeof contactSchema>;

const pillListVariants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.045,
      delayChildren: 0.04,
    },
  },
  exit: {
    opacity: 1,
    transition: {
      staggerChildren: 0.025,
      staggerDirection: -1,
    },
  },
};

const pillItemVariants = {
  hidden: {
    opacity: 0,
    y: -8,
    scale: 0.94,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.22,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  },
  exit: {
    opacity: 0,
    y: -4,
    scale: 0.97,
    transition: {
      duration: 0.14,
    },
  },
};

type NoteStepId =
  | 'name'
  | 'intent'
  | 'company'
  | 'job'
  | 'project'
  | 'budget'
  | 'contact';

type NoteFlow = 'project' | 'join';

type ContactFocusField = 'email' | 'phone' | 'precision';

type NoteSnapshot = {
  formValues: FormData;
  precision: string;
  activeStepIndex: number;
  maxReachedStep: number;
  showIntentPicker: boolean;
  showProjectPicker: boolean;
  showBudgetPicker: boolean;
  contactFocusField: ContactFocusField;
};

type SidebarThread = {
  id: string;
  title: string;
  date: string;
  isEditable?: boolean;
  href?: string;
  snapshot?: NoteSnapshot;
};

function useRadialHoverBackground<T extends HTMLElement>() {
  const elementRef = useRef<T>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback((event: React.MouseEvent<T>) => {
    if (!elementRef.current || !backgroundRef.current) return;

    const element = elementRef.current;
    const background = backgroundRef.current;
    const elementWidth = element.getBoundingClientRect().width;
    const x = event.clientX - element.getBoundingClientRect().left - elementWidth / 2;
    const y = event.clientY - element.getBoundingClientRect().top - elementWidth / 2;

    background.style.left = `${x}px`;
    background.style.top = `${y}px`;
  }, []);

  return { elementRef, backgroundRef, handleMouseMove };
}

interface IntentPillButtonProps {
  label: string;
  isSelected: boolean;
  onClick: () => void;
}

function IntentPillButton({ label, isSelected, onClick }: IntentPillButtonProps) {
  const { elementRef, backgroundRef, handleMouseMove } = useRadialHoverBackground<HTMLButtonElement>();

  return (
    <button
      ref={elementRef}
      type="button"
      className={cn([styles.intentPill, isSelected && styles.intentPillSelected])}
      onMouseMove={handleMouseMove}
      onClick={onClick}
    >
      <span className={styles.intentPillInner}>{label}</span>
      <div ref={backgroundRef} className={styles.intentPillBackground} aria-hidden="true" />
    </button>
  );
}

interface SidebarThreadRowProps {
  thread: SidebarThread;
  isActive: boolean;
  onSelect: (threadId: string) => void;
  onInteract?: () => void;
}

function SidebarThreadRow({ thread, isActive, onSelect, onInteract }: SidebarThreadRowProps) {
  const { elementRef, backgroundRef, handleMouseMove } = useRadialHoverBackground<HTMLElement>();
  const threadClasses = cn([
    styles.threadRow,
    styles.threadRowButton,
    !isActive && styles.threadRowInteractive,
    isActive && styles.threadRowActive,
  ]);
  const threadInner = (
    <div className={styles.threadRowInner}>
      <p className={styles.threadTitle}>{thread.title}</p>
      <p className={styles.threadDate}>{thread.date}</p>
    </div>
  );
  const hoverBackground = !isActive && (
    <div ref={backgroundRef} className={styles.threadRowBackground} aria-hidden="true" />
  );

  if (thread.isEditable) {
    return (
      <button
        ref={elementRef as Ref<HTMLButtonElement>}
        type="button"
        className={threadClasses}
        onMouseMove={!isActive ? handleMouseMove : undefined}
        onClick={() => {
          onSelect(thread.id);
          onInteract?.();
        }}
      >
        {threadInner}
        {hoverBackground}
      </button>
    );
  }

  if (thread.href) {
    return (
      <Link
        ref={elementRef as Ref<HTMLAnchorElement>}
        to={thread.href}
        className={threadClasses}
        onMouseMove={!isActive ? handleMouseMove : undefined}
        onClick={() => onInteract?.()}
      >
        {threadInner}
        {hoverBackground}
      </Link>
    );
  }

  return (
    <div className={cn([styles.threadRow, isActive && styles.threadRowActive])}>
      {threadInner}
    </div>
  );
}

const INITIAL_SIDEBAR_THREADS: SidebarThread[] = [
  { id: 'salut', title: '👋 Salut ZOL !', date: '13/04/2026', isEditable: true },
  { id: 'annonces', title: 'Les petites annonces', date: '13/04/2300', href: '/jobs' },
  { id: 'qui', title: 'Zol t\'es qui ?', date: '01/01/2016', href: '/agence' },
  { id: 'clients', title: 'Nos ZOLis clients', date: '13/04/2026', href: '/references' },
  { id: 'doudous', title: 'Les doudous', date: '01/01/2016', href: '/doudous' },
];

const defaultValues: FormData = {
  firstname: '',
  lastname: '',
  email: '',
  job: '',
  company: '',
  phone: '',
  expertise: '',
  type: '',
  budget: '',
  comment: '',
  marketingConsent: false,
  isUrgent: false,
  files: [],
};

const PROJECT_STEPS: NoteStepId[] = [
  'name', 'intent', 'company', 'job', 'project', 'budget', 'contact',
];

const JOIN_STEPS: NoteStepId[] = [
  'name', 'intent', 'contact',
];

const INTENT_CHOICES = [
  { id: 'project', label: 'Vous parler d\'un projet' },
  { id: 'join', label: 'Rejoindre l\'aventure ZOL' },
] as const;

const PROJECT_TYPE_CHOICES = [
  { id: 'strategy', label: 'stratégie ou transformation digitale' },
  { id: 'ecommerce', label: 'e-commerce ou market place' },
  { id: 'mobile', label: 'application mobile' },
  { id: 'tool', label: 'outil métier' },
  { id: 'extranet', label: 'extranet' },
  { id: 'corporate', label: 'site corporate' },
  { id: 'seo', label: 'web analyse, SEO ou RGPD' },
  { id: 'other', label: 'autre' },
] as const;

const BUDGET_CHOICES = [
  { id: 'ric-rac', label: 'je suis ric-rac (10 à 25 k€)' },
  { id: 'medium-low', label: 'j\'ai un peu de budget (25 à 50 k€)' },
  { id: 'medium', label: 'j\'ai un joli budget (50 à 100 k€)' },
  { id: 'high', label: 'j\'ai du budget à la hauteur de mes ambitions (100 à 200 k€)' },
  { id: 'unlimited', label: 'l\'argent ne sera pas un problème (au-delà de 200 k€)' },
] as const;

const FORM_MIN_WIDTH = 520;
const FORM_MIN_HEIGHT = 400;
const FORM_MAX_HEIGHT = 700;
const SIDEBAR_MAX_WIDTH = 356;
const SIDEBAR_COLLAPSE_THRESHOLD = 200;
const SIDEBAR_WIDTH_RATIO = SIDEBAR_MAX_WIDTH / 1019;
const SIDEBAR_OVERLAY_WIDTH_PERCENT = 75;

function formatNoteDate(date: Date): string {
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function createEmptySnapshot(): NoteSnapshot {
  return {
    formValues: { ...defaultValues },
    precision: '',
    activeStepIndex: 0,
    maxReachedStep: 0,
    showIntentPicker: false,
    showProjectPicker: false,
    showBudgetPicker: false,
    contactFocusField: 'email',
  };
}

function resolveExpertiseFromChoice(choiceLabel: string, expertises: Option[]): string {
  const match = expertises.find(
    (item) => item.label.toLowerCase().trim() === choiceLabel.toLowerCase().trim(),
  );
  return match ? String(match.value) : choiceLabel;
}

function getExpertiseDisplayLabel(expertiseValue: string, expertises: Option[]): string {
  if (!expertiseValue) return '';

  const fromCms = expertises.find((item) => String(item.value) === expertiseValue);
  if (fromCms?.label && fromCms.label.toLowerCase() !== 'expertise') {
    return fromCms.label;
  }

  const fromChoice = INTENT_CHOICES.find(
    (item) => item.label.toLowerCase() === expertiseValue.toLowerCase(),
  );
  if (fromChoice) return fromChoice.label;

  if (expertiseValue.toLowerCase() === 'expertise') return '';

  return expertiseValue;
}

function resolveExpertiseForSubmit(value: string, expertises: Option[]): string {
  const byValue = expertises.find((item) => String(item.value) === value);
  if (byValue) return String(byValue.value);

  const byLabel = expertises.find(
    (item) => item.label.toLowerCase().trim() === value.toLowerCase().trim(),
  );
  if (byLabel) return String(byLabel.value);

  return value;
}


function setRefValue<T>(ref: React.MutableRefObject<T | null>, value: T | null): void {
  // eslint-disable-next-line no-param-reassign
  ref.current = value;
}

interface ContactInteractiveFormProps {
  expertises: ContactType['expertises'];
  origins: ContactType['origins'];
  className?: string;
}

function getNoteFlow(expertiseValue: string, expertises: Option[]): NoteFlow | null {
  const label = getExpertiseDisplayLabel(expertiseValue, expertises).toLowerCase();
  if (!label) return null;
  if (label.includes('aventure') || label.includes('rejoindre')) return 'join';
  return 'project';
}

function getStepsForFlow(flow: NoteFlow | null): NoteStepId[] {
  if (!flow) return ['name', 'intent'];
  return flow === 'join' ? JOIN_STEPS : PROJECT_STEPS;
}

function focusElement(element: HTMLElement | null | undefined) {
  if (!element) return;
  requestAnimationFrame(() => {
    element.focus();
  });
}

interface InlineBlinkInputProps extends InputHTMLAttributes<HTMLInputElement> {
  value: string;
  inputRef?: Ref<HTMLInputElement>;
  isActive?: boolean;
}

function InlineBlinkInput({ value, inputRef, className, isActive = true, ...props }: InlineBlinkInputProps) {
  const showUnderscore = !value && isActive;

  return (
    <span className={cn([styles.inlineFieldWrap, value && styles.inlineFieldFilled])}>
      {showUnderscore && <span className={styles.blinkUnderscore} aria-hidden="true">_</span>}
      <input
        {...props}
        value={value}
        className={cn([
          styles.inlineInput,
          showUnderscore && styles.inlineInputOverlay,
          value && styles.inlineInputFilled,
          className,
        ])}
        ref={inputRef}
        autoComplete="off"
      />
    </span>
  );
}

function usePromptReveal(shouldAnimate: boolean) {
  const [prefixDone, setPrefixDone] = useState(!shouldAnimate);

  useEffect(() => {
    setPrefixDone(!shouldAnimate);
  }, [shouldAnimate]);

  return { prefixDone, setPrefixDone };
}

function useSuffixReveal(shouldAnimate: boolean) {
  const [suffixDone, setSuffixDone] = useState(!shouldAnimate);

  useEffect(() => {
    setSuffixDone(!shouldAnimate);
  }, [shouldAnimate]);

  return { suffixDone, setSuffixDone };
}

interface AnimatedIntroProps {
  shouldAnimate: boolean;
  onIntroComplete?: () => void;
}

interface NameStepLineProps extends AnimatedIntroProps {
  isActive: boolean;
  firstname: string;
  onGoToStep: () => void;
  firstnameField: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'ref'>;
  firstnameRef: (el: HTMLInputElement | null) => void;
  activeInputRef: React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>;
}

function NameStepLine({
  shouldAnimate,
  onIntroComplete,
  isActive,
  firstname,
  onGoToStep,
  firstnameField,
  firstnameRef,
  activeInputRef,
}: NameStepLineProps) {
  const { t } = useTranslation();
  const { prefixDone, setPrefixDone } = usePromptReveal(shouldAnimate);

  useEffect(() => {
    if (prefixDone && isActive) {
      focusElement(activeInputRef.current);
    }
  }, [activeInputRef, isActive, prefixDone]);

  return (
    <p className={styles.noteLine}>
      <span className={styles.promptEmoji}>👋 </span>
      <TypewriterText
        text={t('contact.interactive.greeting')}
        enabled={shouldAnimate}
        onComplete={() => setPrefixDone(true)}
      />
      {prefixDone && (
        isActive ? (
          <InlineBlinkInput
            value={firstname}
            aria-label={t('contact.form.firstname')}
            {...firstnameField}
            inputRef={(el) => {
              firstnameRef(el);
              setRefValue(activeInputRef, el);
              if (isActive) focusElement(el);
            }}
          />
        ) : (
          firstname && (
            <button type="button" className={styles.noteValue} onClick={onGoToStep}>
              {firstname}
            </button>
          )
        )
      )}
      {prefixDone && (
        <TypewriterText
          text={t('contact.interactive.note.nameAfter')}
          enabled={shouldAnimate && prefixDone}
          onComplete={onIntroComplete}
        />
      )}
    </p>
  );
}

interface TextInputStepLineProps extends AnimatedIntroProps {
  isActive: boolean;
  value: string;
  beforeText: string;
  afterText?: string;
  ariaLabel: string;
  onGoToStep: () => void;
  field: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'ref'>;
  fieldRef: (el: HTMLInputElement | null) => void;
  activeInputRef: React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>;
}

function TextInputStepLine({
  shouldAnimate,
  onIntroComplete,
  isActive,
  value,
  beforeText,
  afterText,
  ariaLabel,
  onGoToStep,
  field,
  fieldRef,
  activeInputRef,
}: TextInputStepLineProps) {
  const { prefixDone, setPrefixDone } = usePromptReveal(shouldAnimate);
  const { setSuffixDone } = useSuffixReveal(shouldAnimate);

  const handlePrefixComplete = () => {
    setPrefixDone(true);
    if (!afterText) onIntroComplete?.();
  };

  const handleSuffixComplete = () => {
    setSuffixDone(true);
    onIntroComplete?.();
  };

  useEffect(() => {
    if (prefixDone && isActive) {
      focusElement(activeInputRef.current);
    }
  }, [activeInputRef, isActive, prefixDone]);

  return (
    <p className={styles.noteLine}>
      <TypewriterText
        text={beforeText}
        enabled={shouldAnimate}
        onComplete={handlePrefixComplete}
      />
      {prefixDone && (
        isActive ? (
          <InlineBlinkInput
            value={value}
            aria-label={ariaLabel}
            {...field}
            inputRef={(el) => {
              fieldRef(el);
              setRefValue(activeInputRef, el);
              if (isActive) focusElement(el);
            }}
          />
        ) : (
          value && (
            <button type="button" className={styles.noteValue} onClick={onGoToStep}>
              {value}
            </button>
          )
        )
      )}
      {afterText && prefixDone && (
        <TypewriterText
          text={afterText}
          enabled={shouldAnimate && prefixDone}
          onComplete={handleSuffixComplete}
        />
      )}
    </p>
  );
}

interface PickerStepLineProps extends AnimatedIntroProps {
  isActive: boolean;
  promptText: string;
  selectedLabel: string;
  isPickerOpen: boolean;
  choices: readonly { id: string; label: string }[];
  onOpenPicker: () => void;
  onClosePicker: () => void;
  onSelect: (label: string) => void;
  onClear: () => void;
}

function PickerStepLine({
  shouldAnimate,
  onIntroComplete,
  isActive,
  promptText,
  selectedLabel,
  isPickerOpen,
  choices,
  onOpenPicker,
  onClosePicker,
  onSelect,
  onClear,
}: PickerStepLineProps) {
  const { prefixDone, setPrefixDone } = usePromptReveal(shouldAnimate);
  const addButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (prefixDone && isActive && !isPickerOpen && !selectedLabel) {
      focusElement(addButtonRef.current);
    }
  }, [isActive, isPickerOpen, prefixDone, selectedLabel]);

  return (
    <div className={cn([styles.intentBlock, isPickerOpen && styles.intentBlockOpen])}>
      <div className={styles.intentRow}>
        <TypewriterText
          text={promptText}
          enabled={shouldAnimate}
          className={styles.intentPrompt}
          onComplete={() => {
            setPrefixDone(true);
            onIntroComplete?.();
          }}
        />
        {prefixDone && !isPickerOpen && selectedLabel && (
          <button type="button" className={styles.noteValue} onClick={onClear}>
            {selectedLabel}
          </button>
        )}
        {prefixDone && isActive && !selectedLabel && (
          <span className={styles.intentPickerAnchor}>
            <button
              ref={addButtonRef}
              type="button"
              className={cn([styles.pillToggle, isPickerOpen && styles.pillToggleOpen])}
              onClick={isPickerOpen ? onClosePicker : onOpenPicker}
              aria-label={isPickerOpen ? 'Fermer' : 'Choisir'}
              aria-expanded={isPickerOpen}
            >
              <span className={styles.pillIcon} aria-hidden="true">
                <span className={styles.pillIconBar} />
                <span className={styles.pillIconBar} />
              </span>
            </button>
            <AnimatePresence>
              {isPickerOpen && (
                <motion.span
                  className={styles.pillList}
                  variants={pillListVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  {choices.map((choice) => (
                    <motion.span
                      key={choice.id}
                      className={styles.pillListItem}
                      variants={pillItemVariants}
                    >
                      <IntentPillButton
                        label={choice.label}
                        isSelected={selectedLabel === choice.label}
                        onClick={() => onSelect(choice.label)}
                      />
                    </motion.span>
                  ))}
                </motion.span>
              )}
            </AnimatePresence>
          </span>
        )}
      </div>
    </div>
  );
}

interface ContactStepBlockProps extends AnimatedIntroProps {
  isActive: boolean;
  values: FormData;
  precision: string;
  contactFocusField: ContactFocusField;
  onFocusField: (field: ContactFocusField) => void;
  onPrecisionChange: (value: string) => void;
  onGoToStep: (field: ContactFocusField) => void;
  emailField: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'ref'>;
  emailRef: (el: HTMLInputElement | null) => void;
  phoneField: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'ref'>;
  phoneRef: (el: HTMLInputElement | null) => void;
  contactEmailRef: React.MutableRefObject<HTMLInputElement | null>;
  contactPhoneRef: React.MutableRefObject<HTMLInputElement | null>;
  contactPrecisionRef: React.MutableRefObject<HTMLInputElement | null>;
  control: Control<FormData>;
  marketingConsentError?: string;
  showSubmitActions: boolean;
  isSubmitting: boolean;
}

function ContactStepBlock({
  shouldAnimate,
  onIntroComplete,
  isActive,
  values,
  precision,
  contactFocusField,
  onFocusField,
  onPrecisionChange,
  onGoToStep,
  emailField,
  emailRef,
  phoneField,
  phoneRef,
  contactEmailRef,
  contactPhoneRef,
  contactPrecisionRef,
  control,
  marketingConsentError,
  showSubmitActions,
  isSubmitting,
}: ContactStepBlockProps) {
  const { t } = useTranslation();
  const [emailPromptDone, setEmailPromptDone] = useState(!shouldAnimate);
  const [phonePromptDone, setPhonePromptDone] = useState(!shouldAnimate);
  const [precisionPromptDone, setPrecisionPromptDone] = useState(!shouldAnimate);

  useEffect(() => {
    setEmailPromptDone(!shouldAnimate);
    setPhonePromptDone(!shouldAnimate);
    setPrecisionPromptDone(!shouldAnimate);
  }, [shouldAnimate]);

  const hasPhone = Boolean(values.phone?.trim());
  const hasPrecision = Boolean(precision.trim());

  useEffect(() => {
    if (!isActive) return;

    const refMap = {
      email: contactEmailRef,
      phone: contactPhoneRef,
      precision: contactPrecisionRef,
    };
    const promptDoneMap = {
      email: emailPromptDone,
      phone: phonePromptDone,
      precision: precisionPromptDone,
    };

    if (promptDoneMap[contactFocusField]) {
      focusElement(refMap[contactFocusField].current);
    }
  }, [
    contactEmailRef,
    contactFocusField,
    contactPhoneRef,
    contactPrecisionRef,
    emailPromptDone,
    isActive,
    phonePromptDone,
    precisionPromptDone,
  ]);

  const handleContactEnter = (
    event: React.KeyboardEvent<HTMLInputElement>,
    nextField?: ContactFocusField,
  ) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    event.stopPropagation();
    if (nextField) onFocusField(nextField);
  };

  const renderClickableValue = (label: string, field: ContactFocusField) => (
    <button type="button" className={styles.noteValue} onClick={() => onGoToStep(field)}>
      {label}
    </button>
  );

  return (
    <div className={styles.contactBlock}>
      <p className={styles.noteLine}>
        <TypewriterText
          text={t('contact.interactive.note.emailBefore')}
          enabled={shouldAnimate}
          onComplete={() => setEmailPromptDone(true)}
        />
        {emailPromptDone && (
          isActive ? (
            <InlineBlinkInput
              value={values.email}
              type="email"
              aria-label={t('contact.form.email')}
              isActive={contactFocusField === 'email'}
              {...emailField}
              onFocus={() => onFocusField('email')}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' || event.shiftKey) return;
                event.preventDefault();
                event.stopPropagation();
                if (!values.email.trim()) return;
                onFocusField('phone');
              }}
              inputRef={(el) => {
                emailRef(el);
                setRefValue(contactEmailRef, el);
                if (isActive && contactFocusField === 'email') focusElement(el);
              }}
            />
          ) : (
            values.email && renderClickableValue(values.email, 'email')
          )
        )}
      </p>

      {emailPromptDone && (
        <p className={styles.noteLine}>
          <TypewriterText
            text={t('contact.interactive.note.phoneBefore')}
            enabled={shouldAnimate && emailPromptDone}
            onComplete={() => setPhonePromptDone(true)}
          />
          {phonePromptDone && (
            isActive ? (
              <InlineBlinkInput
                value={values.phone || ''}
                type="tel"
                aria-label={t('contact.form.phone')}
                isActive={contactFocusField === 'phone'}
                {...phoneField}
                onFocus={() => onFocusField('phone')}
                onKeyDown={(event) => handleContactEnter(event, 'precision')}
                inputRef={(el) => {
                  phoneRef(el);
                  setRefValue(contactPhoneRef, el);
                  if (isActive && contactFocusField === 'phone') focusElement(el);
                }}
              />
            ) : (
              hasPhone && renderClickableValue(values.phone!, 'phone')
            )
          )}
        </p>
      )}

      {phonePromptDone && (
        <p className={styles.noteLine}>
          <TypewriterText
            text={t('contact.interactive.note.precisionBefore')}
            enabled={shouldAnimate && phonePromptDone}
            onComplete={() => {
              setPrecisionPromptDone(true);
              onIntroComplete?.();
            }}
          />
          {precisionPromptDone && (
            isActive ? (
              <InlineBlinkInput
                value={precision}
                aria-label={t('contact.form.comment')}
                isActive={contactFocusField === 'precision'}
                onChange={(event) => onPrecisionChange(event.target.value)}
                onFocus={() => onFocusField('precision')}
                onKeyDown={(event) => handleContactEnter(event)}
                inputRef={(el) => {
                  setRefValue(contactPrecisionRef, el);
                  if (isActive && contactFocusField === 'precision') focusElement(el);
                }}
              />
            ) : (
              hasPrecision && renderClickableValue(precision, 'precision')
            )
          )}
        </p>
      )}

      {showSubmitActions && isActive && (
        <div className={styles.contactActions}>
          <Controller
            name="marketingConsent"
            control={control}
            render={({ field }) => (
              <ToggleField
                id="marketingConsent"
                name="marketingConsent"
                checked={field.value}
                error={marketingConsentError}
                onChange={(e) => field.onChange(e.target.checked)}
              >
                <p>{t('contact.form.marketingConsent')}</p>
              </ToggleField>
            )}
          />
          <Button
            type="submit"
            color={ColorThemeEnum.ORANGE}
            disabled={isSubmitting}
          >
            {t('contact.form.send')}
          </Button>
        </div>
      )}
    </div>
  );
}

function ContactInteractiveForm({
  expertises,
  origins,
  className,
}: ContactInteractiveFormProps) {
  const { t } = useTranslation();
  const { isMobile } = useResponsiveQueries();
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [maxReachedStep, setMaxReachedStep] = useState(0);
  const [precision, setPrecision] = useState('');
  const [contactFocusField, setContactFocusField] = useState<ContactFocusField>('email');
  const [showIntentPicker, setShowIntentPicker] = useState(false);
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const [showBudgetPicker, setShowBudgetPicker] = useState(false);
  const [formId, setFormId] = useState('');
  const [selectedOrigin, setSelectedOrigin] = useState<string | null>(null);
  const [formStep, setFormStep] = useState<'form' | 'success' | 'thanks'>('form');
  const [exitDirection, setExitDirection] = useState<'left' | 'right'>('left');
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [sidebarThreads, setSidebarThreads] = useState<SidebarThread[]>(INITIAL_SIDEBAR_THREADS);
  const [activeThreadId, setActiveThreadId] = useState('salut');
  const [introAnimatedSteps, setIntroAnimatedSteps] = useState<Set<number>>(new Set());
  const [timestampAnimated, setTimestampAnimated] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const isResizingRef = useRef(false);
  const interactionStartRef = useRef({
    pointerX: 0,
    pointerY: 0,
    posX: 0,
    posY: 0,
    width: 0,
    height: 0,
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    trigger,
  } = useForm<FormData>({
    mode: 'onChange',
    resolver: zodResolver(contactSchema),
    defaultValues,
  });

  const values = watch();
  const emailValue = watch('email');

  const noteFlow = getNoteFlow(values.expertise, expertises);
  const noteSteps = useMemo(() => getStepsForFlow(noteFlow), [noteFlow]);

  const effectiveFormWidth = size?.width ?? null;

  const computedSidebarWidth = useMemo(() => {
    if (!effectiveFormWidth) return SIDEBAR_MAX_WIDTH;
    return Math.min(SIDEBAR_MAX_WIDTH, Math.round(effectiveFormWidth * SIDEBAR_WIDTH_RATIO));
  }, [effectiveFormWidth]);

  const isSidebarCollapsed = isMobile || (
    effectiveFormWidth !== null && computedSidebarWidth < SIDEBAR_COLLAPSE_THRESHOLD
  );

  useEffect(() => {
    if (!isSidebarCollapsed) {
      setIsSidebarOpen(false);
    }
  }, [isSidebarCollapsed]);

  const closeSidebarOverlay = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  const handleDragStart = useCallback((event: React.MouseEvent) => {
    if (event.button !== 0) return;

    event.preventDefault();
    isDraggingRef.current = true;
    setIsDragging(true);
    interactionStartRef.current = {
      ...interactionStartRef.current,
      pointerX: event.clientX,
      pointerY: event.clientY,
      posX: position.x,
      posY: position.y,
    };
  }, [position]);

  const handleResizeStart = useCallback((event: React.MouseEvent) => {
    if (event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();

    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const rect = wrapper.getBoundingClientRect();
    const currentWidth = size?.width ?? rect.width;
    const currentHeight = size?.height ?? rect.height;

    isResizingRef.current = true;
    setIsResizing(true);
    setSize({ width: currentWidth, height: currentHeight });
    interactionStartRef.current = {
      ...interactionStartRef.current,
      pointerX: event.clientX,
      pointerY: event.clientY,
      width: currentWidth,
      height: currentHeight,
    };
  }, [size]);

  useEffect(() => {
    const handlePointerMove = (event: MouseEvent) => {
      const start = interactionStartRef.current;

      if (isDraggingRef.current) {
        setPosition({
          x: start.posX + event.clientX - start.pointerX,
          y: start.posY + event.clientY - start.pointerY,
        });
      }

      if (isResizingRef.current) {
        setSize({
          width: Math.max(FORM_MIN_WIDTH, start.width + event.clientX - start.pointerX),
          height: Math.min(
            FORM_MAX_HEIGHT,
            Math.max(FORM_MIN_HEIGHT, start.height + event.clientY - start.pointerY),
          ),
        });
      }
    };

    const handlePointerUp = () => {
      isDraggingRef.current = false;
      isResizingRef.current = false;
      setIsDragging(false);
      setIsResizing(false);
    };

    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', handlePointerUp);

    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
    };
  }, []);

  const scrollToForm = useCallback(() => {
    const bloc = document.getElementById('contact-form-block');
    if (!bloc) return;
    const smoother = ScrollSmoother.get();
    if (smoother) {
      smoother.scrollTo(bloc, true);
      return;
    }
    bloc.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const mutation = useMutation({
    mutationFn: postContact,
    onError: (error) => {
      console.error('Erreur lors de la soumission du formulaire:', error);
    },
    onSuccess: (data: { data: { documentId: string } }) => {
      setFormId(data.data.documentId);
      setFormStep('success');
      scrollToForm();
    },
  });

  const mutationThanks = useMutation({
    mutationFn: putContactOrigin,
    onError: (error) => {
      console.error('Erreur lors de la soumission du formulaire:', error);
    },
    onSuccess: () => {
      setFormId('');
      setFormStep('thanks');
      scrollToForm();
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      const mergedComment = [data.comment, precision].filter(Boolean).join('\n\n');
      const newData = {
        ...data,
        lastname: data.lastname || '',
        comment: mergedComment || undefined,
        expertise: resolveExpertiseForSubmit(data.expertise || '', expertises),
        type: data.type || undefined,
        budget: data.budget || undefined,
        files: data.files,
      };

      setExitDirection('left');
      await mutation.mutateAsync(newData);
    } catch (error) {
      console.error('Erreur lors de la soumission du formulaire:', error);
    }
  };

  const handleSendOrigin = () => {
    if (selectedOrigin) {
      setExitDirection('right');
      mutationThanks.mutate({ id: formId, origin: selectedOrigin });
    }
  };

  const resetForm = () => {
    setExitDirection('right');
    setFormStep('form');
    setActiveStepIndex(0);
    setMaxReachedStep(0);
    setPrecision('');
    setContactFocusField('email');
    setShowIntentPicker(false);
    setShowProjectPicker(false);
    setShowBudgetPicker(false);
    setIntroAnimatedSteps(new Set());
    setTimestampAnimated(false);
    reset();
  };

  const activeInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const contactEmailRef = useRef<HTMLInputElement>(null);
  const contactPhoneRef = useRef<HTMLInputElement>(null);
  const contactPrecisionRef = useRef<HTMLInputElement>(null);
  const currentStepId = noteSteps[activeStepIndex];
  const showSendButton = currentStepId === 'contact' && emailValue.trim().length > 0;

  const focusContactField = useCallback((field: ContactFocusField) => {
    setContactFocusField(field);
  }, []);

  const closeAllPickers = useCallback(() => {
    setShowIntentPicker(false);
    setShowProjectPicker(false);
    setShowBudgetPicker(false);
  }, []);

  const goToStep = useCallback((index: number, focusField?: ContactFocusField) => {
    setActiveStepIndex(index);
    if (focusField) setContactFocusField(focusField);
    closeAllPickers();
  }, [closeAllPickers]);

  const isStepReadyToAdvance = useCallback((stepId: NoteStepId, formValues: FormData) => {
    switch (stepId) {
    case 'name':
      return formValues.firstname.trim().length > 0;
    case 'intent':
      return formValues.expertise.length > 0;
    case 'company':
      return Boolean(formValues.company?.trim());
    case 'job':
      return Boolean(formValues.job?.trim());
    case 'project':
      return Boolean(formValues.type?.length);
    case 'budget':
      return Boolean(formValues.budget?.length);
    case 'contact':
      return false;
    default:
      return false;
    }
  }, []);

  const goToNextStep = useCallback(async () => {
    const stepId = noteSteps[activeStepIndex];
    if (!stepId) return;

    const fieldsByStep: Partial<Record<NoteStepId, (keyof FormData)[]>> = {
      name: ['firstname'],
      intent: ['expertise'],
      company: ['company'],
      job: ['job'],
      project: ['type'],
      budget: ['budget'],
    };

    const fields = fieldsByStep[stepId];
    if (fields?.length) {
      const isValid = await trigger(fields);
      if (!isValid) return;
    }

    if (stepId === 'contact') return;

    const nextIndex = Math.min(activeStepIndex + 1, noteSteps.length - 1);
    const nextStepId = noteSteps[nextIndex];
    setActiveStepIndex(nextIndex);
    setMaxReachedStep((prev) => Math.max(prev, nextIndex));
    closeAllPickers();
    if (nextStepId === 'contact') setContactFocusField('email');
  }, [activeStepIndex, closeAllPickers, noteSteps, trigger]);

  useEffect(() => {
    const isPickerOrContactStep = currentStepId === 'contact'
      || currentStepId === 'intent'
      || currentStepId === 'project'
      || currentStepId === 'budget';
    if (isPickerOrContactStep) return;

    focusElement(activeInputRef.current);
  }, [activeStepIndex, currentStepId]);

  useEffect(() => {
    if (activeStepIndex >= noteSteps.length) {
      setActiveStepIndex(noteSteps.length - 1);
    }
  }, [activeStepIndex, noteSteps.length]);

  const clearIntentDownstreamFields = useCallback(() => {
    setValue('company', '');
    setValue('job', '');
    setValue('comment', '');
    setValue('type', '');
    setValue('budget', '');
    setPrecision('');
  }, [setValue]);

  const handleIntentSelect = (choiceLabel: string) => {
    const expertiseValue = resolveExpertiseFromChoice(choiceLabel, expertises);
    const previousFlow = getNoteFlow(values.expertise, expertises);
    const nextFlow = getNoteFlow(expertiseValue, expertises);

    setValue('expertise', expertiseValue, { shouldValidate: true });
    setShowIntentPicker(false);

    if (previousFlow !== nextFlow) {
      clearIntentDownstreamFields();
    }

    const nextSteps = getStepsForFlow(nextFlow);
    const nextIndex = nextSteps.indexOf('intent') + 1;
    setActiveStepIndex(nextIndex);
    setMaxReachedStep((prev) => Math.max(prev, nextIndex));
    if (nextSteps[nextIndex] === 'contact') setContactFocusField('email');
  };

  const handleIntentClear = useCallback((stepIndex: number) => {
    setValue('expertise', '');
    setShowIntentPicker(true);
    setShowProjectPicker(false);
    setShowBudgetPicker(false);
    setActiveStepIndex(stepIndex);
    setMaxReachedStep(stepIndex);
    clearIntentDownstreamFields();
  }, [clearIntentDownstreamFields, setValue]);

  const advanceFromStep = useCallback((fromIndex: number) => {
    const nextIndex = Math.min(fromIndex + 1, noteSteps.length - 1);
    const nextStepId = noteSteps[nextIndex];
    setActiveStepIndex(nextIndex);
    setMaxReachedStep((prev) => Math.max(prev, nextIndex));
    closeAllPickers();
    if (nextStepId === 'contact') setContactFocusField('email');
  }, [closeAllPickers, noteSteps]);

  const handleProjectSelect = useCallback((choiceLabel: string) => {
    setValue('type', choiceLabel, { shouldValidate: true });
    setShowProjectPicker(false);
    advanceFromStep(activeStepIndex);
  }, [activeStepIndex, advanceFromStep, setValue]);

  const handleProjectClear = useCallback((stepIndex: number) => {
    setValue('type', '');
    setValue('budget', '');
    setShowProjectPicker(true);
    setShowBudgetPicker(false);
    setActiveStepIndex(stepIndex);
    setMaxReachedStep(stepIndex);
  }, [setValue]);

  const handleBudgetSelect = useCallback((choiceLabel: string) => {
    setValue('budget', choiceLabel, { shouldValidate: true });
    setShowBudgetPicker(false);
    advanceFromStep(activeStepIndex);
  }, [activeStepIndex, advanceFromStep, setValue]);

  const handleBudgetClear = useCallback((stepIndex: number) => {
    setValue('budget', '');
    setShowBudgetPicker(true);
    setActiveStepIndex(stepIndex);
    setMaxReachedStep(stepIndex);
  }, [setValue]);

  const captureSnapshot = useCallback((): NoteSnapshot => ({
    formValues: { ...values },
    precision,
    activeStepIndex,
    maxReachedStep,
    showIntentPicker,
    showProjectPicker,
    showBudgetPicker,
    contactFocusField,
  }), [
    activeStepIndex,
    contactFocusField,
    maxReachedStep,
    precision,
    showBudgetPicker,
    showIntentPicker,
    showProjectPicker,
    values,
  ]);

  const applySnapshot = useCallback((snapshot: NoteSnapshot) => {
    reset(snapshot.formValues);
    setPrecision(snapshot.precision);
    setActiveStepIndex(snapshot.activeStepIndex);
    setMaxReachedStep(snapshot.maxReachedStep);
    setShowIntentPicker(snapshot.showIntentPicker);
    setShowProjectPicker(snapshot.showProjectPicker);
    setShowBudgetPicker(snapshot.showBudgetPicker);
    setContactFocusField(snapshot.contactFocusField);
    setIntroAnimatedSteps(new Set());
    setTimestampAnimated(false);
  }, [reset]);

  const persistActiveThreadSnapshot = useCallback(() => {
    const snapshot = captureSnapshot();
    setSidebarThreads((prev) => prev.map((thread) => (
      thread.id === activeThreadId && thread.isEditable
        ? { ...thread, snapshot }
        : thread
    )));
  }, [activeThreadId, captureSnapshot]);

  const handleAddNote = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    persistActiveThreadSnapshot();

    const emptySnapshot = createEmptySnapshot();
    const newThread: SidebarThread = {
      id: `note-${Date.now()}`,
      title: '👋 Salut ZOL !',
      date: formatNoteDate(new Date()),
      isEditable: true,
      snapshot: emptySnapshot,
    };

    setSidebarThreads((prev) => [newThread, ...prev]);
    setActiveThreadId(newThread.id);
    applySnapshot(emptySnapshot);
  }, [applySnapshot, persistActiveThreadSnapshot]);

  const handleSelectThread = useCallback((threadId: string) => {
    const thread = sidebarThreads.find((item) => item.id === threadId);
    if (!thread?.isEditable) return;

    if (threadId !== activeThreadId) {
      persistActiveThreadSnapshot();
      setActiveThreadId(threadId);
      applySnapshot(thread.snapshot ?? createEmptySnapshot());
    }

    closeSidebarOverlay();
  }, [activeThreadId, applySnapshot, closeSidebarOverlay, persistActiveThreadSnapshot, sidebarThreads]);

  const markIntroAnimated = useCallback((stepIndex: number) => {
    setIntroAnimatedSteps((prev) => {
      if (prev.has(stepIndex)) return prev;
      const next = new Set(prev);
      next.add(stepIndex);
      return next;
    });
  }, []);

  const shouldAnimateIntro = useCallback(
    (stepIndex: number) => activeStepIndex === stepIndex && !introAnimatedSteps.has(stepIndex),
    [activeStepIndex, introAnimatedSteps],
  );

  useEffect(() => {
    if (introAnimatedSteps.has(activeStepIndex)) return undefined;
    return () => {
      markIntroAnimated(activeStepIndex);
    };
  }, [activeStepIndex, introAnimatedSteps, markIntroAnimated]);

  const renderStepLine = (stepId: NoteStepId, stepIndex: number): ReactNode => {
    const isActive = activeStepIndex === stepIndex;
    const isVisible = stepIndex <= maxReachedStep || isActive;
    const shouldAnimate = shouldAnimateIntro(stepIndex);
    const onIntroComplete = () => markIntroAnimated(stepIndex);

    if (!isVisible && stepIndex > activeStepIndex) return null;

    switch (stepId) {
    case 'name': {
      const { ref: firstnameRef, ...firstnameField } = register('firstname');

      return (
        <NameStepLine
          key={stepId}
          shouldAnimate={shouldAnimate}
          onIntroComplete={onIntroComplete}
          isActive={isActive}
          firstname={values.firstname}
          onGoToStep={() => goToStep(stepIndex)}
          firstnameField={firstnameField}
          firstnameRef={firstnameRef}
          activeInputRef={activeInputRef}
        />
      );
    }
    case 'intent': {
      const expertiseLabel = getExpertiseDisplayLabel(values.expertise, expertises);

      return (
        <PickerStepLine
          key={stepId}
          shouldAnimate={shouldAnimate}
          onIntroComplete={onIntroComplete}
          isActive={isActive}
          promptText={t('contact.interactive.note.intentBefore')}
          selectedLabel={expertiseLabel}
          isPickerOpen={isActive && showIntentPicker}
          choices={INTENT_CHOICES}
          onOpenPicker={() => setShowIntentPicker(true)}
          onClosePicker={() => setShowIntentPicker(false)}
          onSelect={handleIntentSelect}
          onClear={() => handleIntentClear(stepIndex)}
        />
      );
    }
    case 'company': {
      const { ref: companyRef, ...companyField } = register('company');

      return (
        <TextInputStepLine
          key={stepId}
          shouldAnimate={shouldAnimate}
          onIntroComplete={onIntroComplete}
          isActive={isActive}
          value={values.company || ''}
          beforeText={t('contact.interactive.note.profileBefore')}
          ariaLabel={t('contact.form.company')}
          onGoToStep={() => goToStep(stepIndex)}
          field={companyField}
          fieldRef={companyRef}
          activeInputRef={activeInputRef}
        />
      );
    }
    case 'job': {
      const { ref: jobRef, ...jobField } = register('job');

      return (
        <TextInputStepLine
          key={stepId}
          shouldAnimate={shouldAnimate}
          onIntroComplete={onIntroComplete}
          isActive={isActive}
          value={values.job || ''}
          beforeText={t('contact.interactive.note.profileMiddle')}
          afterText={t('contact.interactive.note.profileAfter')}
          ariaLabel={t('contact.form.job')}
          onGoToStep={() => goToStep(stepIndex)}
          field={jobField}
          fieldRef={jobRef}
          activeInputRef={activeInputRef}
        />
      );
    }
    case 'project': {
      const projectLabel = values.type || '';

      return (
        <PickerStepLine
          key={stepId}
          shouldAnimate={shouldAnimate}
          onIntroComplete={onIntroComplete}
          isActive={isActive}
          promptText={t('contact.interactive.note.projectBefore')}
          selectedLabel={projectLabel}
          isPickerOpen={isActive && showProjectPicker}
          choices={PROJECT_TYPE_CHOICES}
          onOpenPicker={() => setShowProjectPicker(true)}
          onClosePicker={() => setShowProjectPicker(false)}
          onSelect={handleProjectSelect}
          onClear={() => handleProjectClear(stepIndex)}
        />
      );
    }
    case 'budget': {
      const budgetLabel = values.budget || '';

      return (
        <PickerStepLine
          key={stepId}
          shouldAnimate={shouldAnimate}
          onIntroComplete={onIntroComplete}
          isActive={isActive}
          promptText={t('contact.interactive.note.budgetBefore')}
          selectedLabel={budgetLabel}
          isPickerOpen={isActive && showBudgetPicker}
          choices={BUDGET_CHOICES}
          onOpenPicker={() => setShowBudgetPicker(true)}
          onClosePicker={() => setShowBudgetPicker(false)}
          onSelect={handleBudgetSelect}
          onClear={() => handleBudgetClear(stepIndex)}
        />
      );
    }
    case 'contact': {
      const { ref: emailRef, ...emailField } = register('email');
      const { ref: phoneRef, ...phoneField } = register('phone');

      return (
        <ContactStepBlock
          key={stepId}
          shouldAnimate={shouldAnimate}
          onIntroComplete={onIntroComplete}
          isActive={isActive}
          values={values}
          precision={precision}
          contactFocusField={contactFocusField}
          onFocusField={focusContactField}
          onPrecisionChange={setPrecision}
          onGoToStep={(field) => goToStep(stepIndex, field)}
          emailField={emailField}
          emailRef={emailRef}
          phoneField={phoneField}
          phoneRef={phoneRef}
          contactEmailRef={contactEmailRef}
          contactPhoneRef={contactPhoneRef}
          contactPrecisionRef={contactPrecisionRef}
          control={control}
          marketingConsentError={errors.marketingConsent?.message}
          showSubmitActions={showSendButton}
          isSubmitting={mutation.isPending}
        />
      );
    }
    default:
      return null;
    }
  };

  const handleFormKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;

    if (currentStepId === 'contact') return;

    event.preventDefault();

    if (!currentStepId || !isStepReadyToAdvance(currentStepId, values)) return;

    goToNextStep().catch(() => undefined);
  };

  const wrapperStyle = {
    transform: `translate(${position.x}px, ${position.y}px)`,
    width: size?.width,
    height: size?.height,
    maxWidth: size ? 'none' : undefined,
  };

  const sidebarStyle = {
    flexBasis: computedSidebarWidth,
    width: computedSidebarWidth,
    maxWidth: computedSidebarWidth,
  };

  const sidebarContent = (
    <>
      <div
        className={styles.sidebarHeader}
        onMouseDown={handleDragStart}
        role="presentation"
        aria-label="Déplacer le formulaire"
      >
        <div className={styles.sidebarWindowDots}>
          <span className={styles.closeDot} />
          <span className={styles.hideDot} />
          <span className={styles.fullScreenDot} />
        </div>
        <button
          type="button"
          className={styles.sidebarAddNoteButton}
          onClick={handleAddNote}
          onMouseDown={(event) => event.stopPropagation()}
          aria-label="Ajouter une note"
        >
          <Picto icon="addBox" width={24} height={24} className={styles.sidebarIcon} />
        </button>
      </div>

      <div className={styles.pinnedRow}>
        <Picto icon="pushpinDown" width={24} height={24} className={styles.pinnedIcon} />
        <span>{t('contact.interactive.pinned')}</span>
      </div>

      {sidebarThreads.map((thread) => (
        <SidebarThreadRow
          key={thread.id}
          thread={thread}
          isActive={activeThreadId === thread.id}
          onSelect={handleSelectThread}
          onInteract={isSidebarCollapsed ? closeSidebarOverlay : undefined}
        />
      ))}
    </>
  );

  return (
    <div
      ref={wrapperRef}
      id="contact-form-block"
      className={cn([
        styles.wrapper,
        size && styles.wrapperSized,
        (isDragging || isResizing) && styles.wrapperInteracting,
        className,
      ])}
      style={wrapperStyle}
    >
      <AnimatePresence mode="wait" initial={false}>
        {formStep === 'form' && (
          <motion.div
            key="form"
            className={styles.shell}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={exitDirection === 'left' ? { opacity: 0, x: '-20%' } : { opacity: 0, x: '20%' }}
            transition={{ duration: 0.25 }}
          >
            {!isSidebarCollapsed && (
              <aside className={styles.sidebar} style={sidebarStyle} aria-hidden="true">
                {sidebarContent}
              </aside>
            )}

            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <form
              className={cn([styles.panel, isSidebarCollapsed && styles.panelWithNotesToggle])}
              onSubmit={handleSubmit(onSubmit)}
              onKeyDown={handleFormKeyDown}
              noValidate
            >
              {isSidebarCollapsed && (
                <button
                  type="button"
                  className={styles.notesToggleButton}
                  onClick={() => setIsSidebarOpen(true)}
                  aria-label="Afficher les notes"
                  aria-expanded={isSidebarOpen}
                >
                  <Picto icon="pushpinDown" width={24} height={24} className={styles.notesToggleIcon} />
                </button>
              )}

              <p
                className={styles.timestamp}
                onMouseDown={handleDragStart}
                role="presentation"
                aria-label="Déplacer le formulaire"
              >
                <TypewriterText
                  text={t('contact.interactive.timestamp')}
                  enabled={!timestampAnimated}
                  onComplete={() => setTimestampAnimated(true)}
                />
              </p>

              <div className={styles.messages}>
                {noteSteps.map((stepId, index) => {
                  if (index > maxReachedStep) return null;
                  return renderStepLine(stepId, index);
                })}
              </div>

              {mutation.isError && (
                <div className={styles.apiError}>
                  <p>{t('contact.form.error')}</p>
                </div>
              )}
            </form>

            <AnimatePresence>
              {isSidebarCollapsed && isSidebarOpen && (
                <>
                  <motion.button
                    type="button"
                    className={styles.sidebarBackdrop}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    onClick={closeSidebarOverlay}
                    aria-label="Fermer les notes"
                  />
                  <motion.aside
                    className={cn([styles.sidebar, styles.sidebarOverlay])}
                    style={{ width: `${SIDEBAR_OVERLAY_WIDTH_PERCENT}%` }}
                    initial={{ x: '-100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '-100%' }}
                    transition={{ duration: 0.3, ease: [0.65, 0.04, 0.27, 0.78] }}
                    aria-hidden="true"
                  >
                    {sidebarContent}
                  </motion.aside>
                </>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {formStep === 'success' && (
          <motion.div
            key="success"
            className={styles.success}
            initial={{ opacity: 0, x: '20%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '-20%' }}
            transition={{ duration: 0.25 }}
          >
            <div
              className={styles.successDragBar}
              onMouseDown={handleDragStart}
              role="presentation"
              aria-label="Déplacer le formulaire"
            >
              <span className={styles.closeDot} />
              <span className={styles.hideDot} />
              <span className={styles.fullScreenDot} />
            </div>

            <MixedTitle
              size="h2"
              sizeSpan={null}
              className={styles.successTitle}
              title="<span>Merci</span>"
              seoTitleTag="h2"
              color={ColorThemeEnum.BLACK}
              colorSpan={ColorThemeEnum.ORANGE}
            />
            <p className={styles.successText}>
              <span>{t('contact.form.successMessageText.first')}</span>
              <span>{t('contact.form.successMessageText.second')}</span>
            </p>

            <div className={styles.origins}>
              {origins.map((origin) => (
                <OptionButton
                  color={ColorThemeEnum.PURPLE}
                  key={origin.value}
                  type="button"
                  isSelected={selectedOrigin === origin.value}
                  onClick={() => setSelectedOrigin(origin.value)}
                >
                  {origin.label}
                </OptionButton>
              ))}
            </div>

            <div className={styles.actions}>
              <Button color={ColorThemeEnum.ORANGE} onClick={handleSendOrigin} disabled={!selectedOrigin}>
                {selectedOrigin ? t('contact.form.send') : t('contact.form.chooseOption')}
              </Button>
            </div>
          </motion.div>
        )}

        {formStep === 'thanks' && (
          <ThankYouMessage
            title={t('contact.form.thankYouMessage.title')}
            message={t('contact.form.thankYouMessage.message')}
            buttonText={t('contact.form.sendAnotherMessage')}
            onReset={resetForm}
          />
        )}
      </AnimatePresence>

      <button
        type="button"
        className={styles.resizeHandle}
        onMouseDown={handleResizeStart}
        aria-label="Redimensionner le formulaire"
      />
    </div>
  );
}

export default ContactInteractiveForm;
